import React, { useEffect, useRef } from 'react';
import { ArrowDownCircle, RefreshCw } from 'lucide-react';
import { MyTeamsModal, useMyTeamsAnchoredModal } from './MyTeamsModal';

interface DemoteConfirmModalProps {
  show: boolean;
  playerName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isProcessing: boolean;
  language: 'en' | 'fr';
}

export function DemoteConfirmModal({
  show,
  playerName,
  onConfirm,
  onCancel,
  isProcessing,
  language
}: DemoteConfirmModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const anchoredModal = useMyTeamsAnchoredModal();

  useEffect(() => {
    if (show && !anchoredModal && modalRef.current) {
      modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [show, anchoredModal]);

  if (!show) return null;

  const titleText = language === 'fr' ? 'Confirmer la Rétrogradation' : 'Confirm Demotion';
  // Phrases séparées pour insérer directement l'icône
  const firstLine = language === 'fr'
    ? `Êtes-vous certain de vouloir rétrograder ${playerName} ?`
    : `Are you sure you want to demote ${playerName}?`;
  const secondLine = language === 'fr'
    ? <>Si vous voulez simplement interchanger deux joueurs, utilisez le bouton <RefreshCw size={16} className="mx-1 text-primary" /> pour effectuer une permutation (swap) et ne perdre qu'une seule rétrogradation au lieu de deux.</>
    : <>If you only want to swap two players, use the <RefreshCw size={16} className="mx-1 text-primary" /> button to perform a swap so it counts as only one demotion instead of two.</>;
  const noteLine = language === 'fr'
    ? <>Astuce: cliquez sur <RefreshCw size={14} className="mx-1 text-primary" /> d’un joueur du club principal puis sur celui d’un joueur des réserves (ou l’inverse) pour ouvrir la fenêtre de permutation.</>
    : <>Tip: click <RefreshCw size={14} className="mx-1 text-primary" /> on a main club player then on a reserve player (or the reverse) to open the swap dialog.</>;
  const cancelText = language === 'fr' ? 'Annuler' : 'Cancel';
  const confirmText = language === 'fr' ? 'Confirmer la rétrogradation' : 'Confirm Demotion';
  const processingText = language === 'fr' ? 'Traitement en cours...' : 'Processing...';

  return (
    <MyTeamsModal onClose={onCancel} busy={isProcessing}>
      <div
        className="modal fade show"
        style={{
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1050,
          overflow: 'auto'
        }}
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered" ref={modalRef}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center">
                <ArrowDownCircle size={20} className="me-2 text-warning" />
                {titleText}
              </h5>
              <button type="button" className="btn-close" onClick={onCancel}></button>
            </div>
            <div className="modal-body">
              <p className="mb-3">{firstLine}</p>
              <p className="mb-0">{secondLine}</p>
              <div className="alert alert-info mt-3"><small>{noteLine}</small></div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isProcessing}>
                {cancelText}
              </button>
              <button
                type="button"
                className="btn btn-warning d-flex align-items-center"
                onClick={onConfirm}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    {processingText}
                  </>
                ) : (
                  <>
                    <ArrowDownCircle size={16} className="me-2" />
                    {confirmText}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1040 }}
      ></div>
    </MyTeamsModal>
  );
}
